import { useState, useEffect } from 'react';
import FacilityModal from '../../services/components/FacilityModal';

export default function Hero() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showFacilityModal, setShowFacilityModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      if (mobileMenuOpen) setMobileMenuOpen(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [mobileMenuOpen]);

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigateToServices = () => {
    setMobileMenuOpen(false);
    setShowFacilityModal(true);
  };

  return (
    <section className="relative min-h-screen flex flex-col bg-slate-900">
      {showFacilityModal && <FacilityModal onClose={() => setShowFacilityModal(false)} />}

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-slate-900/95 backdrop-blur-md shadow-lg border-b border-yellow-500/20' : 'bg-transparent'
      }`}>
        <div className="px-6 lg:px-8 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <a href="/" className="cursor-pointer">
              <img
                src="https://static.readdy.ai/image/870e0649015e5e428345464cd8040611/e2821367afaf48576bf0fa4cd75aa99b.png"
                alt="Guardian Veterans LLC"
                className="h-[58px] lg:h-[68px] w-auto object-contain"
              />
            </a>
          </div>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-8">
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="text-gray-300 hover:text-yellow-400 transition-colors font-medium text-sm whitespace-nowrap cursor-pointer tracking-wide">
              About
            </button>
            <button onClick={navigateToServices} className="text-gray-300 hover:text-yellow-400 transition-colors font-medium text-sm whitespace-nowrap cursor-pointer tracking-wide">
              Partner with us
            </button>
            <button onClick={() => scrollToSection('facility-form')} className="text-gray-300 hover:text-yellow-400 transition-colors font-medium text-sm whitespace-nowrap cursor-pointer tracking-wide">
              For Facilities
            </button>
            <button onClick={() => scrollToSection('private-pay-form')} className="text-gray-300 hover:text-yellow-400 transition-colors font-medium text-sm whitespace-nowrap cursor-pointer tracking-wide">
              For Patients
            </button>
            <button
              onClick={() => scrollToSection('waitlist')}
              className="px-5 py-2 bg-yellow-500 text-black rounded-md font-bold text-sm hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer"
            >
              Join Waitlist
            </button>
          </div>

          {/* Mobile: phone + hamburger */}
          <div className="flex lg:hidden items-center gap-3">
            <a
              href="tel:+16022956626"
              className="flex items-center gap-1.5 bg-yellow-500/10 border border-yellow-500/30 px-3 py-1.5 rounded-full text-yellow-400 text-xs font-bold whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-sm"></i>
              Call Us
            </a>
            <button
              onClick={() => setMobileMenuOpen(prev => !prev)}
              className="w-10 h-10 flex items-center justify-center text-white cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-2xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-slate-900/98 backdrop-blur-md border-t border-yellow-500/20 px-6 py-4 flex flex-col gap-1">
            <button onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setMobileMenuOpen(false); }} className="text-left text-gray-200 font-medium text-base py-3 border-b border-white/5 cursor-pointer hover:text-yellow-400 transition-colors">
              About
            </button>
            <button onClick={navigateToServices} className="text-left text-gray-200 font-medium text-base py-3 border-b border-white/5 cursor-pointer hover:text-yellow-400 transition-colors">
              Partner with Us
            </button>
            <button onClick={() => scrollToSection('facility-form')} className="text-left text-gray-200 font-medium text-base py-3 border-b border-white/5 cursor-pointer hover:text-yellow-400 transition-colors">
              For Facilities
            </button>
            <button onClick={() => scrollToSection('private-pay-form')} className="text-left text-gray-200 font-medium text-base py-3 border-b border-white/5 cursor-pointer hover:text-yellow-400 transition-colors">
              For Patients
            </button>
            <button
              onClick={() => scrollToSection('waitlist')}
              className="mt-2 w-full py-3 bg-yellow-500 text-black rounded-lg font-bold text-sm hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer"
            >
              Join Waitlist
            </button>
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="flex-1 flex items-stretch min-h-screen">
        {/* Left Side — Text */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-12 lg:px-20 pt-32 pb-20 z-10 relative">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full px-5 py-2 mb-8 w-fit">
            <i className="ri-shield-star-fill text-yellow-400 text-base"></i>
            <span className="text-yellow-400 font-semibold text-sm tracking-wide whitespace-nowrap">Veteran-Owned &amp; Operated</span>
          </div>

          <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-yellow-400 tracking-wide mb-3">Coming Soon!</p>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
            Veteran-Owned<br />
            <span className="text-yellow-400">Non-Emergency</span><br />
            Medical Transportation
          </h1>

          <div className="w-16 h-1 bg-yellow-500 rounded-full mb-6"></div>

          <p className="text-lg text-gray-300 leading-relaxed mb-4 max-w-xl">
            <strong className="text-white">Launching Soon in the West Valley.</strong> Guardian Veterans LLC is preparing to launch dependable Non-Emergency Medical Transportation (NEMT) for seniors, assisted living communities, and healthcare facilities.
          </p>

          <p className="text-base text-gray-400 leading-relaxed mb-10 max-w-xl">
            We are currently gathering transportation needs from facilities and residents in the West Valley before our official service launch.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-4">
            <button
              onClick={() => scrollToSection('facility-form')}
              className="px-7 py-4 bg-yellow-500 text-black rounded-lg font-bold text-base hover:bg-yellow-400 transition-all shadow-lg shadow-yellow-500/20 whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-building-2-line text-lg"></i>
              Facility Transportation Survey
            </button>
            <button
              onClick={() => scrollToSection('waitlist')}
              className="px-7 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-bold text-base hover:bg-white/20 transition-all border border-white/20 hover:border-yellow-500/50 whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-list-check-2 text-lg"></i>
              Join Early Access Waitlist
            </button>
            <button
              onClick={() => scrollToSection('private-pay-form')}
              className="px-7 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-bold text-base hover:bg-white/20 transition-all border border-white/20 hover:border-yellow-500/50 whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-user-heart-line text-lg"></i>
              Private Pay Interest
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center gap-6 mt-12 pt-8 border-t border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-medal-line text-yellow-400 text-xl"></i>
              </div>
              <span className="text-gray-400 text-sm">Veteran Founded</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-map-pin-2-line text-yellow-400 text-xl"></i>
              </div>
              <span className="text-gray-400 text-sm">West Valley, AZ</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-wheelchair-line text-yellow-400 text-xl"></i>
              </div>
              <span className="text-gray-400 text-sm">NEMT Services</span>
            </div>
          </div>
        </div>

        {/* Right Side — Image */}
        <div className="hidden lg:block w-1/2 relative overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_6b098f678455535538486e5d89a8eed7_7e32afeb.jpg"
            alt="Guardian Veterans Non-Emergency Medical Transportation"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/20 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-slate-900/20"></div>

          {/* Floating info card */}
          <div className="absolute bottom-12 right-8 bg-slate-900/80 backdrop-blur-md border border-yellow-500/30 rounded-xl p-5 max-w-xs">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
                <i className="ri-phone-line text-black text-lg"></i>
              </div>
              <div>
                <p className="text-white font-bold text-sm">Call Us</p>
                <p className="text-yellow-400 font-bold">602-295-6626</p>
              </div>
            </div>
            <p className="text-gray-400 text-xs leading-relaxed">
              Serving Sun City, Peoria, Surprise, Glendale &amp; the entire West Valley of Arizona.
            </p>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="flex flex-col items-center gap-2 animate-bounce">
          <span className="text-gray-400 text-xs font-medium tracking-widest uppercase">Scroll</span>
          <i className="ri-arrow-down-line text-yellow-500 text-xl"></i>
        </div>
      </div>
    </section>
  );
}
